<script setup>
import { mapActions, mapState } from 'pinia';
import Avatar from '~/components/settings/Avatar.vue';
import LanguageSwitcher from '~/components/layouts/LanguageSwitcher.vue';
import ThemeSwitcher from '~/components/layouts/ThemeSwitcher.vue';
import { useLocalePath } from '#imports';

const localePath = useLocalePath();
</script>

<template>
  <div class="fixed top-0 z-[999] w-full px-6 py-2">
    <div
      class="relative z-50 mx-auto flex items-center justify-between min-w-fit max-w-full gap-4 rounded-xl px-6 py-2 bg-[var(--p-background-0)]/50 backdrop-blur-sm transition-all duration-500"
      :class="isScrolled ? 'lg:w-[800px] lg:shadow-lg' : 'lg:w-[1024px]'"
    >
      <div class="transition-all duration-500">
        <NuxtLink
          :to="localePath('index')"
          class="max-h-inherit flex min-h-[40px] w-fit items-center"
        >
          <img
            width="40"
            height="40"
            src="/media/logo-blue.webp"
            alt="Cobe1 Logo"
            loading="lazy"
          />
          <h1 class="text-[var(--p-primary-color)] font-bold text-lg whitespace-nowrap tracking-widest transition-all duration-500" :class="isScrolled ? 'lg:w-0 lg:opacity-0' : 'lg:w-[100px] lg:opacity-100'">COBE1</h1>
        </NuxtLink>
      </div>

      <!-- Desktop Navigation -->
      <div class="mx-auto transition-all duration-500">
        <div
          class="hidden items-center gap-4 transition-all duration-500 lg:flex"
        >
          <PrimeButton
            as="router-link"
            :to="localePath('index')"
            :label="$t('navigation.home')"
            icon="pi pi-home"
            severity="contrast"
            style="transition: all 0.5s ease-in-out"
            class="whitespace-nowrap"
            text
            size="small"
          />
          <PrimeButton
            as="router-link"
            :to="
              localePath({
                name: 'profession',
                params: { profession: $t('routes.professions.lawyer') },
              })
            "
            text
            size="small"
            severity="contrast"
            class="whitespace-nowrap"
            style="transition: all 0.5s ease-in-out"
          >
            <Icon name="icon:legal-advice" />
            {{ $t('professions.lawyer.bee') }}
          </PrimeButton>
          <PrimeButton
            as="router-link"
            :to="
              localePath({
                name: 'pricing',
              })
            "
            :label="$t('navigation.pricing')"
            icon="pi pi-wallet"
            text
            size="small"
            severity="contrast"
            class="whitespace-nowrap"
            style="transition: all 0.5s ease-in-out"
          />
          <PrimeButton
            as="router-link"
            :to="
              localePath({
                name: 'contact',
              })
            "
            :label="$t('navigation.contact')"
            icon="pi pi-envelope"
            text
            size="small"
            severity="contrast"
            class="whitespace-nowrap"
            style="transition: all 0.5s ease-in-out"
          />
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>
      </div>

      <div class="transition-all duration-500">
        <!-- Desktop Auth Buttons -->
        <div v-if="!profile" class="hidden gap-2 lg:flex">
          <PrimeButton
            as="router-link"
            :to="localePath('login')"
            :label="isScrolled ? '' : $t('navigation.login')"
            :icon="isScrolled ? 'pi pi-sign-in' : ''"
            size="small"
            style="transition: all 0.5s ease-in-out; white-space: nowrap"
            severity="contrast"
            outlined
            :class="{
              'p-button-icon-only': isScrolled,
              'p-button-sm': isScrolled,
            }"
          />
          <PrimeButton
            as="router-link"
            :to="localePath('register')"
            :label="isScrolled ? '' : $t('navigation.register')"
            :icon="isScrolled ? 'pi pi-user-plus' : ''"
            size="small"
            style="transition: all 0.5s ease-in-out; white-space: nowrap"
            :class="{
              'p-button-icon-only': isScrolled,
              'p-button-sm': isScrolled,
            }"
          />
        </div>
        <div v-else class="hidden items-center lg:flex">
          <div class="w-[70px]" :class="{ 'lg:hidden': isScrolled }"></div>
          <Avatar />
        </div>

        <!-- Mobile Hamburger Button -->
        <button
          :aria-expanded="mobileMenuOpen"
          type="button"
          :class="[
            'hamburger-icon block cursor-pointer focus:outline-none lg:hidden',
            { active: mobileMenuOpen },
          ]"
          @click="toggleMobileMenu"
          text

          aria-label="Toggle menu"
          style="z-index: 50;"
        >
          <span class="line line-1" :class="{ active: mobileMenuOpen }"></span>
          <span class="line line-2" :class="{ active: mobileMenuOpen }"></span>
          <span class="line line-3" :class="{ active: mobileMenuOpen }"></span>
        </button>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div
      class="fixed bottom-0 left-0 z-40 flex h-full w-full flex-col bg-gradient-to-b from-[var(--p-background-0)] to-[var(--p-background-50)] px-6 py-2 backdrop-blur-sm transition-all duration-500 ease-in-out lg:hidden"
      :class="{
        'pointer-events-none opacity-0': !mobileMenuOpen,
        'opacity-100': mobileMenuOpen,
      }"
    >
      <div
        class="flex h-full flex-col justify-between gap-6 overflow-y-auto px-6 pt-[20vh]"
        :class="{
          'translate-y-4 opacity-0': !mobileMenuOpen,
          'translate-y-0 opacity-100 transition-all delay-100 duration-500':
            mobileMenuOpen,
        }"
      >
        <!-- Navigation Links -->
        <div class="flex flex-col items-start justify-center gap-4">
          <PrimeButton
            as="router-link"
            :to="localePath('index')"
            :label="$t('navigation.home')"
            icon="pi pi-home"
            severity="contrast"
            text
            @click="closeMobileMenu"
          />
          <PrimeButton
            as="router-link"
            :to="
              localePath({
                name: 'profession',
                params: { profession: $t('routes.professions.lawyer') },
              })
            "
            severity="contrast"
            text
            @click="closeMobileMenu"
          >
            <Icon name="icon:legal-advice" />
            {{ $t('professions.lawyer.bee') }}
          </PrimeButton>
          <PrimeButton
            as="router-link"
            :to="
              localePath({
                name: 'pricing',
              })
            "
            :label="$t('navigation.pricing')"
            icon="pi pi-wallet"
            severity="contrast"
            text
            @click="closeMobileMenu"
            class="whitespace-nowrap"
            style="transition: all 0.5s ease-in-out"
          />
          <PrimeButton
            as="router-link"
            :to="
              localePath({
                name: 'contact',
              })
            "
            :label="$t('navigation.contact')"
            icon="pi pi-envelope"
            severity="contrast"
            text
            @click="closeMobileMenu"
            class="whitespace-nowrap"
            style="transition: all 0.5s ease-in-out"
          />
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>

        <!-- Auth Buttons -->
        <div class="mb-8 flex flex-col gap-4">
          <template v-if="!profile">
            <PrimeButton
              as="router-link"
              :to="localePath('login')"
              :label="$t('navigation.login')"
              severity="contrast"
              class="w-full justify-center py-3 text-lg"
              @click="closeMobileMenu"
            />
            <PrimeButton
              as="router-link"
              :to="localePath('register')"
              :label="$t('navigation.register')"
              severity="contrast"
              outlined
              class="w-full justify-center py-3 text-lg"
              @click="closeMobileMenu"
            />
          </template>
          <template v-else>
            <div
              class="flex items-center gap-3 rounded-lg bg-[var(--p-background-100)] p-4"
            >
              <Avatar :user="profile" size="large" />
              <div>
                <p class="font-medium">{{ profile.name }}</p>
                <p class="text-sm text-[var(--p-text-color-secondary)]">
                  {{ profile.email }}
                </p>
              </div>
            </div>
            <PrimeButton
              :label="$t('navigation.logout')"
              severity="contrast"
              outlined
              class="w-full justify-center py-3 text-lg"
              @click="handleLogout()"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {
    Avatar,
    LanguageSwitcher,
    ThemeSwitcher,
  },
  data() {
    return {
      mobileMenuOpen: false,
      isScrolled: false,
    };
  },
  computed: {
    ...mapState(useAuthStore, ['profile']),
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    ...mapActions(useAuthStore, ['logout']),
    async handleLogout() {
      const success = await this.logout();
      this.closeMobileMenu();
      if (success) {
        const router = useRouter();
        const localePath = useLocalePath();
        router.push(localePath({ name: 'index' }));
      }
    },
    toggleMobileMenu(isOpen) {
      // If isOpen is provided, use it, otherwise toggle current state
      this.mobileMenuOpen =
        typeof isOpen === 'boolean' ? isOpen : !this.mobileMenuOpen;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
  },
};
</script>

<style scoped>
.hamburger-icon {
  width: 30px;
  height: 24px;
  position: relative;
  z-index: 50;
}

.line {
  display: block;
  background: var(--p-primary-color);
  width: 100%;
  height: 3px;
  position: absolute;
  left: 0;
  border-radius: 4px;
  transition: all 0.4s;
}

.line-1 {
  top: 0;
}

.line-2 {
  top: 50%;
  transform: translateY(-50%);
}

.line-3 {
  bottom: 0;
}

.hamburger-icon:hover .line-1 {
  transform: translateY(-2px);
}

.hamburger-icon:hover .line-3 {
  transform: translateY(2px);
}

.hamburger-icon.active .line-1 {
  transform: translateY(10.5px) rotate(45deg);
}

.hamburger-icon.active .line-2 {
  opacity: 0;
}

.hamburger-icon.active .line-3 {
  transform: translateY(-10.5px) rotate(-45deg);
}
</style>
